import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getModelsOnly, addModelToProject } from '../../services/aiModels';

const AddExistingModelModal = ({ show, onHide, projectId, onModelAdded }) => {
  const [models, setModels] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState('');
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (show) {
      fetchAvailableModels();
    }
  }, [show]);

  const fetchAvailableModels = async () => {
    setLoading(true);
    try {
      // Fetch all available models
      const allModels = await getModelsOnly();
      // For now, show all models - the user can add any model to their project
      // We might want to filter out models already in this project
      setModels(allModels);
    } catch (err) {
      console.error('Error fetching models:', err);
      toast.error('Failed to fetch available models.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddModel = async () => {
    if (!selectedModelId) {
      toast.warning('Please select a model.');
      return;
    }

    setProcessing(true);
    try {
      // Add model to project using the service function
      await addModelToProject(selectedModelId, projectId);
      toast.success('Model added to project successfully!');
      setSelectedModelId('');
      if (onModelAdded) {
        onModelAdded();
      }
      onHide();
    } catch (err) {
      console.error('Error adding model to project:', err);
      const errorMsg = err.response?.data?.detail || err.response?.data?.error || 'Failed to add model to project.';
      toast.error(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => {
    setSelectedModelId('');
    onHide();
  };

  if (!show) return null;

  const getModelStatusBadge = (model) => {
    if (model.build_status === 'built') {
      return <span className="badge bg-success ms-2">Built</span>;
    } else if (model.build_status === 'building') {
      return <span className="badge bg-warning ms-2">Building</span>;
    } else if (model.build_status === 'failed') {
      return <span className="badge bg-danger ms-2">Failed</span>;
    }
    return <span className="badge bg-secondary ms-2">Not Built</span>;
  };

  return (
    <>
      <div className="modal-backdrop show" onClick={handleClose}></div>
      <div className="modal show" style={{ display: 'block', zIndex: 1050 }} tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fa fa-cube me-2"></i>
                Add Existing Model to Project
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleClose}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="alert alert-info">
                <i className="fa fa-info-circle me-2"></i>
                Select an existing AI model to add to this project
              </div>

              {loading ? (
                <div className="text-center py-3">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <div className="form-group">
                  <label className="form-label">Select Model</label>
                  <select
                    className="form-select"
                    value={selectedModelId}
                    onChange={(e) => setSelectedModelId(e.target.value)}
                    disabled={processing}
                  >
                    <option value="">-- Choose a model --</option>
                    {models.map(model => (
                      <option key={model.id} value={model.id}>
                        {model.name} ({model.type || 'Unknown type'})
                        {model.build_status === 'built' ? ' ✓' : ''}
                      </option>
                    ))}
                  </select>

                  {/* Show selected model details */}
                  {selectedModelId && models.find(m => m.id === parseInt(selectedModelId)) && (
                    <div className="mt-3 p-3 border rounded bg-light">
                      {(() => {
                        const selectedModel = models.find(m => m.id === parseInt(selectedModelId));
                        return (
                          <div>
                            <div className="d-flex align-items-center mb-2">
                              <i
                                className={`fa ${selectedModel.icon || 'fa-cube'} me-2`}
                                style={{ color: selectedModel.color || '#6f42c1', fontSize: '1.5rem' }}
                              ></i>
                              <div>
                                <h6 className="mb-0">{selectedModel.name}</h6>
                                {getModelStatusBadge(selectedModel)}
                              </div>
                            </div>
                            {selectedModel.description && (
                              <p className="text-muted small mb-2">{selectedModel.description}</p>
                            )}
                            <div className="small">
                              <strong>Type:</strong> {selectedModel.type || 'N/A'}<br />
                              <strong>Created:</strong> {new Date(selectedModel.created_at).toLocaleDateString()}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {models.length === 0 && (
                    <p className="text-muted small mt-2">
                      <i className="fa fa-exclamation-triangle me-1"></i>
                      No models available. Create a model first from the Models page.
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClose}
                disabled={processing}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAddModel}
                disabled={!selectedModelId || processing || loading}
              >
                {processing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Adding...
                  </>
                ) : (
                  <>
                    <i className="fa fa-plus me-2"></i>
                    Add Model
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddExistingModelModal;
